// src/views/Restaurant/ClientManagement/ReservationManagement/Reservations.tsx
import React, { useState, useEffect, ChangeEvent } from 'react';
import { Button, Form } from 'react-bootstrap';
import { getAvailableReservationSlots } from '../../../../../../../crud/clients_api/client_reservations';
import { toast } from 'react-toastify';
import Calendar from 'react-calendar';
import { FaPen , FaClock} from 'react-icons/fa';
import 'react-calendar/dist/Calendar.css';
import { ReservationFormData,Configuration,Slot } from '../../types';
import './TimeSelector.scss';
import { getLocalSiteConfig } from '../../../../../../../crud/management_api/local_site_configuration';
import { Dropdown } from 'react-bootstrap';


function slotToTime(slot: number) {
  const h = Math.floor(slot / 4), m = (slot % 4) * 15;
  return `${h.toString().padStart(2, '0')}:${m.toString().padStart(2, '0')}`;
}

function renderDate(d: Date) {
  const days = ['Domingo','Lunes','Martes','Miércoles','Jueves','Viernes','Sábado'];
  const months = ['Enero','Febrero','Marzo','Abril','Mayo','Junio','Julio','Agosto','Septiembre','Octubre','Noviembre','Diciembre'];
  return `${days[d.getDay()]} ${d.getDate()} de ${months[d.getMonth()]}`;
}

interface TimeSelectorProps {
    localInfo: any;
    config: Configuration | null;
    formData: ReservationFormData;
    setFormData: React.Dispatch<React.SetStateAction<ReservationFormData>>;
    local_id: string | undefined;
    setStage: React.Dispatch<React.SetStateAction<'time' | 'client' | 'success' | 'error'>>;
}

const TimeSelector: React.FC<TimeSelectorProps> = ({
    localInfo,
    config,
    formData,
    setFormData,
    local_id,
    setStage
}) => {



  const [availableSlots, setAvailableSlots] = useState<Record<string, Slot>>({});
  const [showAvailableTimes, setShowAvailableTimes] = useState(true);
  const [phone, setPhone] = useState<string>('');


  // fetch raw available slots
  useEffect(() => {
    if (!local_id || !formData.date || !formData.numPeople) return;
    const ds = formData.date.toISOString().slice(0, 10);
    getAvailableReservationSlots(
      +local_id,
      ds,
      formData.numPeople,
      null
    )
      .then((r: any) => r.data && setAvailableSlots(r.data))
      .catch(() => { /* ignore */ });
  }, [local_id, formData.date, formData.numPeople]);

  // fetch local site configuration
  useEffect(() => {
    if (!local_id) return;
    getLocalSiteConfig(+local_id)
      .then((r: any) => {
        if (r.data) {
          setPhone(r.data.object.site_phone);
        }
      })
      .catch(() => { /* ignore */ });
  }, [local_id]);

  // dayKey for config
  const dayKey = formData.date
    ? String((formData.date.getDay() || 7))
    : null;

  // allowed slot window from config
  let minSlot = 0, maxSlot = 95;
  if (config && dayKey && config.reservation_days[dayKey]) {
    const ranges = config.reservation_days[dayKey];
    minSlot = Math.min(...ranges.map(r => r[0]));
    maxSlot = Math.max(...ranges.map(r => r[1]));
  }

  // Obtener los rangos configurados para el día seleccionado
  const dayRanges = config && dayKey ? config.reservation_days[dayKey] || [] : [];

  const nowSlot = Math.floor(new Date().getHours() * 4 + new Date().getMinutes() / 15);

  const now = new Date();
  console.log('Hora actual:', now.toLocaleTimeString()); // Muestra la hora actual en formato legible
  const isToday = formData.date?.toDateString() === now.toDateString();


  const currentHour = now.getHours();
  const currentMinute = now.getMinutes();
  
  // Generar todas las horas posibles según los rangos del día
  let hoursSet = new Set(
    dayRanges.flatMap(([start, end]) => {
      let startHour = Math.floor(start / 4);
      const endHour = Math.floor(end / 4);
      if (isToday) {
        startHour = Math.max(startHour, currentHour);
      }
      return Array.from({ length: endHour - startHour + 1 }, (_, i) => startHour + i);
    })
  );
  
  // Si es hoy, filtrar horas que ya no tienen slots disponibles
  if (isToday) {
    const filteredHours = Array.from(hoursSet).filter(hour => {
      // Para cada hora, verificar si hay al menos un slot disponible después del tiempo actual
      const hourSlots = [0, 15, 30, 45]; // Minutos de los slots
      return hourSlots.some(minute => {
        const slotNumber = hour * 4 + minute / 15;
        // Verificar si el slot está en algún rango del día
        const isInRange = dayRanges.some(([start, end]) => slotNumber >= start && slotNumber <= end);
        if (!isInRange) return false;
        
        // Si es la hora actual, verificar que el minuto del slot sea mayor al minuto actual
        if (hour === currentHour) {
          return minute > currentMinute;
        }
        // Si es una hora futura, es válida
        return hour > currentHour;
      });
    });
    hoursSet = new Set(filteredHours);
  }
  
  const allowedHours = Array.from(hoursSet);


  // Filtrar los minutos permitidos según la hora seleccionada y la hora actual
  const allowedMinutes =
    formData.preferredHour !== null
      ? Array.from(
          new Set(
            dayRanges.flatMap(([start, end]) => {
              const startSlot = formData.preferredHour !== null ? formData.preferredHour * 4 : 0;
              const endSlot = startSlot + 3;
              return Array.from({ length: 4 }, (_, i) => startSlot + i)
                .filter(slot => {
                  // Si es hoy y la hora seleccionada es la actual, solo permitir minutos futuros
                  if (isToday && formData.preferredHour === new Date().getHours()) {
                    const currentMinute = new Date().getMinutes();
                    const slotMinute = (slot % 4) * 15;
                    return (
                      slot >= start &&
                      slot <= end &&
                      slot >= nowSlot &&
                      slotMinute > currentMinute
                    );
                  }
                  // Si es hoy y la hora seleccionada es mayor a la actual, permitir todos los minutos
                  if (
                    isToday &&
                    formData.preferredHour !== null &&
                    formData.preferredHour > new Date().getHours()
                  ) {
                    return slot >= start && slot <= end && slot >= nowSlot;
                  }
                  // Si no es hoy, permitir todos los minutos válidos
                  return slot >= start && slot <= end;
                })
                .map(slot => (slot % 4) * 15);
            })
          )
        )
      : [];

  // expand ±w hours until we find any slots
  const getFilteredSlots = (): string[] => {
    if (
      formData.preferredHour === null ||
      formData.preferredMinute === null
    )
      return [];
    const pref =
      formData.preferredHour * 4 + formData.preferredMinute / 15;
    const keys = Object.keys(availableSlots).map(Number).sort((a, b) => a - b);
    // Filtrar slots pasados si es hoy
    let filteredKeys: number[];
    if (!isToday) {
      filteredKeys = keys;
    } else {
      const now = new Date();
      const currentSlot = nowSlot;
      const currentMinute = now.getMinutes();
      filteredKeys = keys.filter(s => {
        if (s > currentSlot) return true;
        if (s < currentSlot) return false;
        // s === currentSlot: solo mostrar si el minuto de inicio del slot es mayor al minuto actual
        const slotMinute = (s % 4) * 15;
        return slotMinute > currentMinute;
      });
    }
    if (filteredKeys.length === 0) return [];
    // Buscar el slot más cercano al preferido
    let closestSlot = filteredKeys[0];
    let minDiff = Infinity;
    filteredKeys.forEach(slot => {
      const diff = Math.abs(slot - pref);
      if (diff < minDiff) {
        minDiff = diff;
        closestSlot = slot;
      }
    });
    const idx = filteredKeys.indexOf(closestSlot);
    let start = idx - 3;
    let end = idx + 3;
    if (start < 0) {
      end += -start;
      start = 0;
    }
    if (end > filteredKeys.length) {
      start -= (end - filteredKeys.length);
      end = filteredKeys.length;
      if (start < 0) start = 0;
    }
    const visibleSlots = filteredKeys.slice(start, end);
    return visibleSlots.map(String);
  };

  // Corregido para React-Bootstrap Dropdown: recibe el valor seleccionado (string)
  const handleHourChange = (selectedKey: string | null) => {
    if (selectedKey === null) return;
    const h = Number(selectedKey);
    const isHourAvailable = allowedHours.includes(h);
    if (!isHourAvailable) {
      toast.error('El local está cerrado en el periodo seleccionado');
      return;
    }
    setFormData(fd => ({
      ...fd,
      preferredHour: isNaN(h) ? null : h,
      preferredMinute: null,
      selectedSlot: null,
      mesa_id: null,
    }));
    setShowAvailableTimes(true);
  };

  const handleMinuteChange = (selectedKey: string | null) => {
    if (selectedKey === null) return;
    const m = Number(selectedKey);
    const isMinuteAvailable = allowedMinutes.includes(m);
    if (!isMinuteAvailable) {
      toast.error('El local está cerrado en el periodo seleccionado');
      return;
    }
    setFormData(fd => ({
      ...fd,
      preferredMinute: isNaN(m) ? null : m,
      selectedSlot: null,
      mesa_id: null,
    }));
    setShowAvailableTimes(true);
  };

  const handleDateChange = (d: any) => {
    const selectedDate = Array.isArray(d) ? d[0] : d;
    const dayKey = String((selectedDate.getDay() || 7)); // Convertir domingo a 7
    const isDayAvailable = (config?.reservation_days[dayKey] ?? []).length > 0;

    setFormData(fd => ({
      ...fd,
      date: selectedDate,
      isDayAvailable, // Guardar si el día está disponible
    }));
  };

  const handleSlotClick = (k: string) => {
    setFormData(fd => ({
      ...fd,
      selectedSlot: Number(k),
      mesa_id: availableSlots[k].mesas_sugeridas[0],
    }));
    setShowAvailableTimes(false);
  };
  const handleReserve = () => setStage('client');

  // hour options constrained to config
  const hours: number[] = [];
  for (let s = minSlot; s <= maxSlot; s += 4) {
    hours.push(Math.floor(s / 4));
  }
  // minute options only those that land in [minSlot,maxSlot]
  const minuteOpts =
    formData.preferredHour !== null
      ? [0, 15, 30, 45].filter(m => {
          const sl = formData.preferredHour! * 4 + m / 15;
          return sl >= minSlot && sl <= maxSlot;
        })
      : [];

  const filtered = getFilteredSlots();

  return (
        <>
          <h2>Reservas {localInfo?.local_name}</h2>

            <Form.Group>
            <Dropdown
              onSelect={(selectedKey: string | null) => {
                if (selectedKey === null) return;
                setFormData(fd => ({
                  ...fd,
                  numPeople: Number(selectedKey),
                }));
              }}
            >
              <Dropdown.Toggle variant="none" className='dropdown-num-people' id="dropdown-num-people">
                {formData.numPeople} {formData.numPeople > 1 ? 'Personas' : 'Persona'}
              </Dropdown.Toggle>
              <Dropdown.Menu style={{backgroundColor:'white', width: '100%', textAlign: 'center'}}>
                {[...Array(10)].map((_, i) => (
                  <Dropdown.Item key={i + 1} eventKey={(i + 1).toString()}>
                    {i + 1} {i > 0 ? 'Personas' : 'Persona'}
                  </Dropdown.Item>
                ))}
              </Dropdown.Menu>
            </Dropdown>
            </Form.Group>

            {formData.numPeople >= 10 ? (
            <p className="contact-local-msg">
              Para reservas de más de 10 personas, por favor contacte directamente con el local al {phone}.
            </p>
            ) : (
            <Form.Group className="client-reservation-calendar-group">
              <Calendar
              value={formData.date}
              onChange={handleDateChange}
              minDate={new Date()}
              className="styled-calendar restaurant"
              />
            </Form.Group>
            )}

            {formData.date && !formData.isDayAvailable && (
            <p style={{ color: 'red', marginTop: '10px' }}>
              El local está cerrado en el periodo seleccionado.
            </p>
            )}

            {formData.date && config && formData.isDayAvailable && (
            <>
              <Form.Group controlId="formPreferredTime">
                <div className="time-selector-container">
                  <FaClock className="clock-icon" />
                  <div className='time-selector-label'>
                    <Dropdown onSelect={handleHourChange as any}>
                      <Dropdown.Toggle variant="none" id="dropdown-hour">
                        {formData.preferredHour !== null
                        ? formData.preferredHour.toString().padStart(2, '0')
                        : 'HH'}
                      </Dropdown.Toggle>
                    <Dropdown.Menu>
                      {allowedHours.map(h => (
                      <Dropdown.Item key={h} eventKey={h.toString()}>
                        {h.toString().padStart(2, '0')}
                      </Dropdown.Item>
                      ))}
                    </Dropdown.Menu>
                    </Dropdown>
                    <span className="ts-sep">:</span>
                    <Dropdown
                    onSelect={handleMinuteChange as any}
                    >
                      <Dropdown.Toggle
                        variant="secondary"
                        id="dropdown-minute"
                        disabled={formData.preferredHour === null}
                      >
                        {formData.preferredMinute !== null
                        ? formData.preferredMinute.toString().padStart(2, '0')
                        : 'MM'}
                      </Dropdown.Toggle>
                      <Dropdown.Menu>
                        {allowedMinutes.map(m => (
                        <Dropdown.Item key={m} eventKey={m.toString()}>
                          {m.toString().padStart(2, '0')}
                        </Dropdown.Item>
                        ))}
                      </Dropdown.Menu>
                    </Dropdown>
                  </div>
                </div>
              </Form.Group>

              {(formData.preferredHour === null ||
              formData.preferredMinute === null) ? (
              <p className="select-time-prompt">
              Por favor, seleccione primero su hora preferida.
              </p>
              ) : filtered.length ? (
              showAvailableTimes ? (
              <div className="available-times-container">
                {filtered
                .filter(k => 
                  !isToday || +k >= nowSlot // Filtrar slots pasados solo si es hoy
                )
                .map(k => (
                  <div
                  key={k}
                  className={`time-slot ${
                    formData.selectedSlot === +k ? 'selected' : ''
                  }`}
                  onClick={() => {
                    const selectedHour = Math.floor(+k / 4);
                    const selectedMinute = (+k % 4) * 15;
                    setFormData(fd => ({
                    ...fd,
                    preferredHour: selectedHour,
                    preferredMinute: selectedMinute,
                    selectedSlot: Number(k),
                    mesa_id: availableSlots[k].mesas_sugeridas[0], // Use the first suggested mesa
                    }));
                    setShowAvailableTimes(false);
                  }}
                  >
                  {slotToTime(+k)}
                  </div>
                ))}
              </div>
              ) : (
              <div className="selected-slot-container">
              <span>
                {renderDate(formData.date!)} –{' '}
                {slotToTime(formData.selectedSlot!)}
              </span>
              <FaPen
                className="edit-icon"
                onClick={() => setShowAvailableTimes(true)}
              />
              </div>
              )
              ) : (
              <p className="no-slots-msg">
                No hay franjas alrededor de{' '}
                {`${formData.preferredHour
                .toString()
                .padStart(2, '0')}:${formData.preferredMinute
                .toString()
                .padStart(2, '0')}`}.
              </p>
              )}

              {formData.selectedSlot !== null && (
              <Button
              variant='none'
                className="reserve-button"
                onClick={handleReserve}
              >
                Continuar
              </Button>
              )}
            </>
            )}
          </>
  );
};

export default TimeSelector;